import React, { useEffect, useRef, useState } from 'react';
import {
  IonButton,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import { closeOutline } from 'ionicons/icons';
import { barChartOutline, pieChartOutline, analyticsOutline } from 'ionicons/icons';
import { AgCharts, AllCommunityModule, ModuleRegistry } from 'ag-charts-community';
import { ReportRow } from '@/services/ReportingService';
import InspectionAggregatedChartUtil, {
  AggregatedXAxisKey,
  AggregatedYMetric,
  DateInterval,
} from '@/services/util/charting/InspectionAggregatedChartUtil';

ModuleRegistry.registerModules([AllCommunityModule]);

export type ChartSeriesType = 'bar' | 'line' | 'area' | 'pie';

export interface ChartModalConfig {
  chartType: ChartSeriesType;
  xColumn: AggregatedXAxisKey;
  xColumnLabel: string;
  yMetric: AggregatedYMetric;
  yMetricLabel: string;
  dateInterval: DateInterval;
  pieDonutRatio?: number;
  pieOuterRadiusRatio?: number;
}

interface ChartSeriesModalProps {
  isOpen: boolean;
  selectedRows: ReportRow[];
  config: ChartModalConfig;
  onConfigChange: (config: ChartModalConfig) => void;
  onClose: () => void;
}

const X_COLUMN_OPTIONS: Array<{ label: string; value: AggregatedXAxisKey }> = [
  { label: 'LineItem', value: 'lineItem' },
  { label: 'Site', value: 'siteName' },
  { label: 'Area Of Expertise', value: 'areaOfExpertise' },
  { label: 'Inspector', value: 'inspectorName' },
  { label: 'Status', value: 'status' },
  { label: 'Group', value: 'group' },
  { label: 'InspectedOn', value: 'inspectedOn' },
];

const Y_METRIC_OPTIONS: Array<{ label: string; value: AggregatedYMetric }> = [
  { label: 'Count Of Inspections', value: 'inspectionCount' },
//   { label: 'Total Of Inspections', value: 'inspectionTotal' },
  { label: 'Average Score Of Inspections', value: 'inspectionAverage' },
//   { label: 'Composite Score', value: 'compositeScore' },
  { label: 'Count Of Checklist Items', value: 'checklistItemCount' },
  { label: 'Count Of Corrective Actions', value: 'correctiveActionCount' },
  { label: 'Count Of Attachments', value: 'attachmentCount' },
];

const DATE_INTERVAL_OPTIONS: Array<{ label: string; value: DateInterval }> = [
  { label: 'Day', value: 'day' },
  { label: 'Month', value: 'month' },
  { label: 'Year', value: 'year' },
];

const getDefaultConfig = (): ChartModalConfig => ({
  chartType: 'bar',
  xColumn: 'lineItem',
  xColumnLabel: 'Line Item',
  yMetric: 'inspectionCount',
  yMetricLabel: 'Count Of Inspections',
  dateInterval: 'month',
  pieDonutRatio: 0,
  pieOuterRadiusRatio: 1,
});

const ChartSeriesModal: React.FC<ChartSeriesModalProps> = ({
  isOpen,
  selectedRows,
  config,
  onConfigChange,
  onClose,
}) => {
  const chartHostRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<ReturnType<typeof AgCharts.create> | null>(null);
  const [localConfig, setLocalConfig] = useState<ChartModalConfig>(config ?? getDefaultConfig());
  const [isModalReady, setIsModalReady] = useState(false);
  const [hasChartData, setHasChartData] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setLocalConfig(config ?? getDefaultConfig());
  }, [isOpen, config]);

  useEffect(() => {
    if (!isOpen || !isModalReady || !chartHostRef.current) {
      return;
    }

    let isCancelled = false;

    const renderChart = () => {
      if (isCancelled || !chartHostRef.current) {
        return;
      }

      const chartOptions = InspectionAggregatedChartUtil.buildChartOptions(selectedRows, localConfig);
      const chartData = Array.isArray(chartOptions.data) ? chartOptions.data : [];

      setHasChartData(chartData.length > 0);

      if (chartData.length === 0) {
        if (chartRef.current) {
          chartRef.current.destroy();
          chartRef.current = null;
        }
        return;
      }

      chartOptions.container = chartHostRef.current;

      if (!chartRef.current) {
        chartRef.current = AgCharts.create(chartOptions);
      } else {
        chartRef.current.update(chartOptions);
      }
    };

    requestAnimationFrame(renderChart);

    return () => {
      isCancelled = true;
      if (!isOpen && chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [isOpen, isModalReady, localConfig, selectedRows]);

  useEffect(() => {
    if (!isOpen) {
      setIsModalReady(false);
      setHasChartData(false);
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    }
  }, [isOpen]);

  const updateConfig = (updater: (prev: ChartModalConfig) => ChartModalConfig) => {
    setLocalConfig(prev => {
      const updated = updater(prev);
      onConfigChange(updated);
      return updated;
    });
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidPresent={() => setIsModalReady(true)}
      onDidDismiss={onClose}
      style={{ '--width': '980px', '--height': '90vh', '--max-height': '95vh' }}
    >
      <IonHeader>
        <IonToolbar>
          <IonTitle>Charts</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent>
        <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
            <IonItem>
              {/* <IonLabel position="stacked">Chart Type</IonLabel> */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', paddingTop: '8px' }}>
                <IonButton
                  size="small"
                  fill={localConfig.chartType === 'bar' ? 'solid' : 'outline'}
                  onClick={() => updateConfig(prev => ({ ...prev, chartType: 'bar' }))}
                  title="Bar"
                  style={{ margin: '2px' }}
                >
                  <IonIcon icon={barChartOutline} slot="start" />
                  Bar
                </IonButton>
                <IonButton
                  size="small"
                  fill={localConfig.chartType === 'line' ? 'solid' : 'outline'}
                  onClick={() => updateConfig(prev => ({ ...prev, chartType: 'line' }))}
                  title="Line"
                  style={{ margin: '2px' }}
                >
                  <IonIcon icon={analyticsOutline} slot="start" />
                  Line
                </IonButton>
                {/* <IonButton
                  size="small"
                  fill={localConfig.chartType === 'area' ? 'solid' : 'outline'}
                  onClick={() => updateConfig(prev => ({ ...prev, chartType: 'area' }))}
                  title="Area"
                  style={{ margin: '2px' }}
                >
                  <IonIcon icon={barChart} slot="start" />
                  Area
                </IonButton> */}
                <IonButton
                  size="small"
                  fill={localConfig.chartType === 'pie' ? 'solid' : 'outline'}
                  onClick={() => updateConfig(prev => ({ ...prev, chartType: 'pie' }))}
                  title="Pie"
                  style={{ margin: '2px' }}
                >
                  <IonIcon icon={pieChartOutline} slot="start" />
                  Pie
                </IonButton>
              </div>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">X Column</IonLabel>
              <IonSelect
                placeholder="Select X Column"
                value={localConfig.xColumn}
                onIonChange={event => updateConfig(prev => ({ 
                    ...prev, xColumn: event.detail.value as AggregatedXAxisKey, 
                    xColumnLabel: X_COLUMN_OPTIONS.find(opt => opt.value === event.detail.value)?.label ?? prev.xColumnLabel 
                }))}
              >
                {X_COLUMN_OPTIONS.map(option => (
                  <IonSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Y Column (Aggregated)</IonLabel>
              <IonSelect
                placeholder="Select Y Metric"
                value={localConfig.yMetric}
                onIonChange={event => updateConfig(prev => ({ 
                    ...prev, yMetric: event.detail.value as AggregatedYMetric,
                    yMetricLabel: Y_METRIC_OPTIONS.find(opt => opt.value === event.detail.value)?.label ?? prev.yMetricLabel}))}
              >
                {Y_METRIC_OPTIONS.map(option => (
                  <IonSelectOption key={option.value} value={option.value}>
                    {option.label}
                  </IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>

            {localConfig.xColumn === 'inspectedOn' && (
              <IonItem>
                <IonLabel position="stacked">Date Interval</IonLabel>
                <IonSelect
                  value={localConfig.dateInterval}
                  onIonChange={event => updateConfig(prev => ({ ...prev, dateInterval: event.detail.value as DateInterval }))}
                >
                  {DATE_INTERVAL_OPTIONS.map(option => (
                    <IonSelectOption key={option.value} value={option.value}>
                      {option.label}
                    </IonSelectOption>
                  ))}
                </IonSelect>
              </IonItem>
            )}
          </div>

          {localConfig.chartType === 'pie' && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '12px',
              background: 'var(--ion-color-light-tint)',
              border: '1px solid var(--ion-color-light-shade)',
              borderRadius: '8px',
              padding: '10px 12px',
            }}>
              <div>
                <IonLabel style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                  Donut hole — {Math.round((localConfig.pieDonutRatio ?? 0) * 100)}%
                </IonLabel>
                <input
                  type="range"
                  min={0}
                  max={0.9}
                  step={0.05}
                  value={localConfig.pieDonutRatio ?? 0}
                  onChange={event => updateConfig(prev => ({ ...prev, pieDonutRatio: Number(event.target.value) }))}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <IonLabel style={{ fontSize: '0.85rem', fontWeight: 600, display: 'block', marginBottom: '4px' }}>
                  Outer radius — {Math.round((localConfig.pieOuterRadiusRatio ?? 1) * 100)}%
                </IonLabel>
                <input
                  type="range"
                  min={0.3}
                  max={1}
                  step={0.05}
                  value={localConfig.pieOuterRadiusRatio ?? 1}
                  onChange={event => updateConfig(prev => ({ ...prev, pieOuterRadiusRatio: Number(event.target.value) }))}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
          )}

          <div style={{
            border: '1px solid var(--ion-color-light-shade)',
            borderRadius: '8px',
            minHeight: '320px',
            background: 'white',
            padding: '8px',
            position: 'relative',
          }}>
            {!hasChartData && (
              <div style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--ion-color-medium)',
                fontSize: '0.95rem',
                textAlign: 'center',
                padding: '16px',
              }}>
                No chartable data available for the current selection.
              </div>
            )}
            <div ref={chartHostRef} style={{ width: '100%', height: '100%', minHeight: '300px' }} />
          </div>
        </div>
      </IonContent>

      <IonFooter>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', padding: '12px 16px' }}>
          <IonButton fill="outline" color="medium" onClick={onClose}>
            <IonIcon icon={closeOutline} slot="start" />
            Close
          </IonButton>
        </div>
      </IonFooter>
    </IonModal>
  );
};

export default ChartSeriesModal;
